"use client";

import {
	RiCalendarEventLine,
	RiFilter3Line,
	RiPlayListLine,
} from "@remixicon/react";
import type { ProcessedSet, Review } from "@workspace/types";
import { getB2BDisplayParts, REVIEW_FILTER_TYPES } from "@workspace/types";
import { Card, CardContent } from "@workspace/ui/components/card";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { DaySelector } from "@/components/features/festival/navigation/day-selector";
import { MyListCard } from "./mylist/my-list-card";

// Remove internal store usage - component is controlled by external props

// Helper functions for dynamic empty state content
const getEmptyStateIcon = (reviewFilter: string, selectedDay: string) => {
	if (selectedDay !== "all") return RiCalendarEventLine;
	if (reviewFilter !== "all") return RiFilter3Line;
	return RiPlayListLine;
};

const getEmptyStateTitle = (
	reviewFilter: string,
	selectedDay: string,
	readOnly: boolean,
) => {
	if (reviewFilter === "all" && selectedDay === "all") {
		return readOnly ? "No Sets in List" : "Your List Is Empty";
	}
	if (reviewFilter !== "all" && selectedDay === "all") {
		const filterNames = {
			[REVIEW_FILTER_TYPES.WANT]: "Want to See",
			[REVIEW_FILTER_TYPES.MUST]: "Must See",
			[REVIEW_FILTER_TYPES.SAW_LIVE]: "Saw Live",
			[REVIEW_FILTER_TYPES.SAW_STREAM]: "Saw Stream",
			[REVIEW_FILTER_TYPES.RATED]: "Rated",
			[REVIEW_FILTER_TYPES.LOVED]: "Loved",
		};
		return `No ${filterNames[reviewFilter as keyof typeof filterNames] || reviewFilter} Sets`;
	}
	if (reviewFilter === "all" && selectedDay !== "all") {
		return `No Sets for ${selectedDay.charAt(0).toUpperCase() + selectedDay.slice(1)}`;
	}
	const filterNames = {
		[REVIEW_FILTER_TYPES.WANT]: "Want to See",
		[REVIEW_FILTER_TYPES.MUST]: "Must See",
		[REVIEW_FILTER_TYPES.SAW_LIVE]: "Saw Live",
		[REVIEW_FILTER_TYPES.SAW_STREAM]: "Saw Stream",
		[REVIEW_FILTER_TYPES.RATED]: "Rated",
		[REVIEW_FILTER_TYPES.LOVED]: "Loved",
	};
	return `No ${filterNames[reviewFilter as keyof typeof filterNames] || reviewFilter} Sets for ${selectedDay.charAt(0).toUpperCase() + selectedDay.slice(1)}`;
};

const getEmptyStateDescription = (
	reviewFilter: string,
	selectedDay: string,
	readOnly: boolean,
) => {
	if (reviewFilter === "all" && selectedDay === "all") {
		return readOnly
			? "No sets have been added to the list yet."
			: "Start exploring and add sets to your schedule to see them here.";
	}
	if (reviewFilter !== "all" && selectedDay === "all") {
		const filterNames = {
			[REVIEW_FILTER_TYPES.WANT]: "Want to See",
			[REVIEW_FILTER_TYPES.MUST]: "Must See",
			[REVIEW_FILTER_TYPES.SAW_LIVE]: "Saw Live",
			[REVIEW_FILTER_TYPES.SAW_STREAM]: "Saw Stream",
			[REVIEW_FILTER_TYPES.RATED]: "Rated",
			[REVIEW_FILTER_TYPES.LOVED]: "Loved",
		};
		return `No sets found matching the "${filterNames[reviewFilter as keyof typeof filterNames] || reviewFilter}" filter.`;
	}
	if (reviewFilter === "all" && selectedDay !== "all") {
		return `No sets found for ${selectedDay.charAt(0).toUpperCase() + selectedDay.slice(1)}. Try selecting a different day.`;
	}
	const filterNames = {
		[REVIEW_FILTER_TYPES.WANT]: "Want to See",
		[REVIEW_FILTER_TYPES.MUST]: "Must See",
		[REVIEW_FILTER_TYPES.SAW_LIVE]: "Saw Live",
		[REVIEW_FILTER_TYPES.SAW_STREAM]: "Saw Stream",
		[REVIEW_FILTER_TYPES.RATED]: "Rated",
		[REVIEW_FILTER_TYPES.LOVED]: "Loved",
	};
	return `No ${filterNames[reviewFilter as keyof typeof filterNames] || reviewFilter} sets found for ${selectedDay.charAt(0).toUpperCase() + selectedDay.slice(1)}.`;
};

type SortOption =
	| "time-asc"
	| "time-desc"
	| "artist-asc"
	| "artist-desc"
	| "rating-desc"
	| "rating-asc";

interface MyListTabProps {
	reviews: Review[];
	processedSets: ProcessedSet[];
	savedSchedule: string[];
	onStartReview: (setId: string) => void;
	reviewFilter: string;
	readOnly?: boolean;
	hideControls?: boolean;
	externalDay?: string;
	externalSort?: string;
}

export const MyListTab = memo(function MyListTab({
	reviews,
	processedSets,
	savedSchedule,
	onStartReview,
	reviewFilter,
	readOnly = false,
	hideControls = false,
	externalDay,
	externalSort,
}: MyListTabProps) {
	// Use local state initialized from external props
	const [selectedDay, setSelectedDay] = useState(externalDay || "all");
	const [sortOption, setSortOption] = useState<SortOption>(
		(externalSort as SortOption) || "time-asc",
	);

	// Memoize event handler
	const handleStartReview = useCallback(
		(setId: string) => {
			onStartReview(setId);
		},
		[onStartReview],
	);

	// Get available days from processedSets dynamically
	const availableDays = useMemo(() => {
		const uniqueDays = [...new Set(processedSets.map((set) => set.day))];
		// Sort by common festival day order
		return uniqueDays.sort((a, b) => {
			const dayOrder = {
				friday: 0,
				saturday: 1,
				sunday: 2,
				monday: 3,
				tuesday: 4,
				wednesday: 5,
				thursday: 6,
			};
			return (
				(dayOrder[a as keyof typeof dayOrder] || 99) -
				(dayOrder[b as keyof typeof dayOrder] || 99)
			);
		});
	}, [processedSets]);

	// Filter reviews based on the selected category (already handled by parent)
	// and the selected day
	const filteredReviews = useMemo(() => {
		const currentFestivalSetIds = processedSets.map((set) => set.id);

		// First filter to only include reviews for sets in this festival
		// The reviews passed in are already filtered by the parent component
		let reviewsMatchingFilter = reviews.filter((review) =>
			currentFestivalSetIds.includes(review.setId),
		);

		// Apply review category filter - handle both constant and string values
		switch (reviewFilter) {
			case REVIEW_FILTER_TYPES.WANT:
			case "want":
				reviewsMatchingFilter = reviewsMatchingFilter.filter(
					(review) =>
						!review.sawLive &&
						!review.sawStream &&
						!review.mustSee &&
						savedSchedule.includes(review.setId),
				);
				break;
			case REVIEW_FILTER_TYPES.MUST:
			case "must":
				reviewsMatchingFilter = reviewsMatchingFilter.filter(
					(review) => review.mustSee,
				);
				break;
			case REVIEW_FILTER_TYPES.SAW_LIVE:
			case "saw-live":
				reviewsMatchingFilter = reviewsMatchingFilter.filter(
					(review) => review.sawLive,
				);
				break;
			case REVIEW_FILTER_TYPES.SAW_STREAM:
			case "saw-stream":
				reviewsMatchingFilter = reviewsMatchingFilter.filter(
					(review) => review.sawStream,
				);
				break;
			case REVIEW_FILTER_TYPES.RATED:
			case "rated":
				reviewsMatchingFilter = reviewsMatchingFilter.filter(
					(review) => review.rating > 0,
				);
				break;
			case REVIEW_FILTER_TYPES.LOVED:
			case "loved":
				reviewsMatchingFilter = reviewsMatchingFilter.filter(
					(review) => review.loved,
				);
				break;
			default:
				// Don't filter further - show all reviews that match the base criteria
				break;
		}

		// Then filter by selected day
		if (selectedDay !== "all") {
			reviewsMatchingFilter = reviewsMatchingFilter.filter((review) => {
				const set = processedSets.find((s) => s.id === review.setId);
				return set?.day === selectedDay;
			});
		}

		return reviewsMatchingFilter;
	}, [reviews, processedSets, savedSchedule, selectedDay, reviewFilter]);

	// Sort the filtered reviews based on the selected sort option
	const sortedReviews = useMemo(() => {
		return [...filteredReviews].sort((a, b) => {
			const setA = processedSets.find((s) => s.id === a.setId);
			const setB = processedSets.find((s) => s.id === b.setId);

			if (!setA || !setB) return 0;

			switch (sortOption) {
				case "time-asc": {
					// Handle null/undefined start_time for lineup-only festivals
					if (!setA.start_time && !setB.start_time) return 0;
					if (!setA.start_time) return 1; // Push null times to end
					if (!setB.start_time) return -1;
					return (
						new Date(setA.start_time).getTime() -
						new Date(setB.start_time).getTime()
					);
				}
				case "time-desc": {
					// Handle null/undefined start_time for lineup-only festivals
					if (!setA.start_time && !setB.start_time) return 0;
					if (!setA.start_time) return 1; // Push null times to end
					if (!setB.start_time) return -1;
					return (
						new Date(setB.start_time).getTime() -
						new Date(setA.start_time).getTime()
					);
				}
				case "artist-asc": {
					const nameA =
						getB2BDisplayParts(setA).mainText || setA.artist?.name || "";
					const nameB =
						getB2BDisplayParts(setB).mainText || setB.artist?.name || "";
					return nameA.localeCompare(nameB, "en-US", {
						sensitivity: "base",
						numeric: true,
					});
				}
				case "artist-desc": {
					const nameA =
						getB2BDisplayParts(setA).mainText || setA.artist?.name || "";
					const nameB =
						getB2BDisplayParts(setB).mainText || setB.artist?.name || "";
					return nameB.localeCompare(nameA, "en-US", {
						sensitivity: "base",
						numeric: true,
					});
				}
				case "rating-desc":
					return b.rating - a.rating;
				case "rating-asc":
					return a.rating - b.rating;
				default:
					return 0;
			}
		});
	}, [filteredReviews, processedSets, sortOption]);

	// Count sets by day for the tab badges - independent of selectedDay
	const dayCounts = useMemo(() => {
		// First apply the parent's review filter (reviewFilter prop)
		let reviewsMatchingFilter = reviews.filter((review) => {
			const currentFestivalSetIds = processedSets.map((set) => set.id);
			return (
				currentFestivalSetIds.includes(review.setId) &&
				(review.rating > 0 ||
					(review.content && review.content.trim() !== "") ||
					review.loved ||
					review.sawLive ||
					review.sawStream ||
					review.mustSee ||
					(savedSchedule.includes(review.setId) &&
						!review.sawLive &&
						!review.sawStream &&
						!review.mustSee))
			);
		});

		// Apply review category filter - handle both constant and string values
		switch (reviewFilter) {
			case REVIEW_FILTER_TYPES.WANT:
			case "want":
				reviewsMatchingFilter = reviewsMatchingFilter.filter(
					(review) =>
						!review.sawLive &&
						!review.sawStream &&
						!review.mustSee &&
						savedSchedule.includes(review.setId),
				);
				break;
			case REVIEW_FILTER_TYPES.MUST:
			case "must":
				reviewsMatchingFilter = reviewsMatchingFilter.filter(
					(review) => review.mustSee,
				);
				break;
			case REVIEW_FILTER_TYPES.SAW_LIVE:
			case "saw-live":
				reviewsMatchingFilter = reviewsMatchingFilter.filter(
					(review) => review.sawLive,
				);
				break;
			case REVIEW_FILTER_TYPES.SAW_STREAM:
			case "saw-stream":
				reviewsMatchingFilter = reviewsMatchingFilter.filter(
					(review) => review.sawStream,
				);
				break;
			case REVIEW_FILTER_TYPES.RATED:
			case "rated":
				reviewsMatchingFilter = reviewsMatchingFilter.filter(
					(review) => review.rating > 0,
				);
				break;
			case REVIEW_FILTER_TYPES.LOVED:
			case "loved":
				reviewsMatchingFilter = reviewsMatchingFilter.filter(
					(review) => review.loved,
				);
				break;
			default:
				// Don't filter further - show all reviews that match the base criteria
				break;
		}

		const counts: Record<string, number> = {
			total: reviewsMatchingFilter.length,
		};

		// Initialize counts for all available days
		for (const day of availableDays) {
			counts[day] = 0;
		}

		for (const review of reviewsMatchingFilter) {
			const set = processedSets.find((s) => s.id === review.setId);
			if (set?.day && counts[set.day] !== undefined) {
				counts[set.day] = (counts[set.day] || 0) + 1;
			}
		}

		return counts;
	}, [reviews, processedSets, savedSchedule, reviewFilter, availableDays]);

	// Update selectedDay and sortOption when external values change
	useEffect(() => {
		if (externalDay !== undefined) {
			setSelectedDay(externalDay);
		}
	}, [externalDay]);

	useEffect(() => {
		if (externalSort !== undefined) {
			setSortOption(externalSort as SortOption);
		}
	}, [externalSort]);

	return (
		<div className="space-y-4">
			{/* Day filter tabs and sorting dropdown - only shown if not hiding controls */}
			{!hideControls && (
				<div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
					<DaySelector
						availableDays={availableDays}
						selectedDay={selectedDay}
						onDayChange={setSelectedDay}
						getDayCount={(day) => {
							if (day === "total") return dayCounts.total || 0;
							return dayCounts[day] || 0;
						}}
						includeAllOption={true}
						allOptionLabel="All"
						className="w-full"
					/>
				</div>
			)}

			{/* List of cards */}
			<div className="space-y-3">
				{sortedReviews.length === 0 ? (
					<Card>
						<CardContent className="p-8 text-center">
							<div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
								{(() => {
									const IconComponent = getEmptyStateIcon(
										reviewFilter,
										selectedDay,
									);
									return (
										<IconComponent className="h-6 w-6 text-muted-foreground" />
									);
								})()}
							</div>
							<h3 className="mb-1 font-semibold text-xl">
								{getEmptyStateTitle(reviewFilter, selectedDay, readOnly)}
							</h3>
							<p className="text-muted-foreground text-sm">
								{getEmptyStateDescription(reviewFilter, selectedDay, readOnly)}
							</p>
						</CardContent>
					</Card>
				) : (
					sortedReviews.map((review) => {
						const set = processedSets.find((s) => s.id === review.setId);
						if (!set) return null;

						return (
							<MyListCard
								key={review.setId}
								review={review}
								set={set}
								savedSchedule={savedSchedule}
								onStartReview={handleStartReview}
							/>
						);
					})
				)}
			</div>
		</div>
	);
});

MyListTab.displayName = "MyListTab";
